<template>
  <div class="heatmap-report">
    <div class="fixed_content">
      <HeatmapTimeMachine :aid="aid" :report-data="selectedReportData" :basic-info="basicInfoResult" @segment-change="onSegmentChange" @image-loaded="onImageLoaded" />
    </div>
    <scroll-content class="scroll_content" ref="container">
      <div class="reporting-heatmap-analysis">
        <ReportTimeMachine
          ref="reportTimeMachine"
          :aid="aid"
          :timeMachineData="timeMachineData"
          :topEventData="topEventResult"
          :timezoneName="applyData.timezoneName"
          :timezone="applyData.timezone"
          :image-data="captureImageData"
          :replay-data="replayResult"
          :segment="currentSegment"
          @select-report="onSelectReport"
          @add-reservation="onAddReservation"
          @add-reservation-with-credit="onAddReservationWithCredit"
          @duplicate-date="onDuplicateDate"
          @modify-reservation="onModifyReservation"
          @delete-reservation="onDeleteReservation"
          @create-report="onCreateReport"
        />
        <back-to-top v-if="!isLoading" refName="container"/>
      </div>
      <div class="scroll_content-loading">
        <ui-loading-circle v-if="isLoading" :applyBackground="true"/>
      </div>
    </scroll-content>
    <AppDialog
      v-if="popupType === 'success' || popupType === 'reportSuccess' || popupType === 'error'"
      :custom-data="popupData"
      :ok="$t('reporting.timeMachine.popup.confirm')"
      :yes="$t('reporting.timeMachine.popup.confirm')"
      @onCloseClick="onPopupClose"
    />
    <AppDialog
      v-if="popupType === 'duplicateDate'"
      :custom-data="duplicateDateData"
      :cancel="$t('reporting.timeMachine.reservationModal.cancel')"
      :yes="$t('reporting.timeMachine.popup.confirm')"
      @onCloseClick="onDuplicateDateResponse"
    />
    <TimeMachineJamPaymentModal
      v-if="popupType === 'jamPayment'"
      :jam-cost="jamCost"
      :reservation-data="pendingReservation"
      @close="onJamPaymentClose"
      @back="onJamPaymentBack"
      @confirm="onJamPaymentConfirm"
      @charge-jam="onChargeJam"
    />
    <Dimmed v-if="isShowReportPayment">
      <AiReportPayment
        :report-info="applyData"
        :plan-info="reportPlan"
        :report-name="reportDefaultName"
        report-type="segment"
        :apiParamsData="reportApiParams"
        :column-data="timemachineColumns"
        @payment="onReportPayment"
        @close="isShowReportPayment = false"
        @onChargeJam="onReportChargeJam"
      />
    </Dimmed>
    <Dimmed v-if="isChargingJam">
      <ChargePv
        :topUpJam="isChargingJam"
        :userData="userData"
        :planData="planData"
        :remainJam="remainJam"
        @closeLayer="isChargingJam = false"
      />
    </Dimmed>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getTimeMachineList,
  addTimeMachine,
  modifyTimeMachine,
  deleteTimeMachine,
  getReplay,
  getTopEvent,
  createTimeMachineReport,
  createCXReport,
  getBasicInfo
} from '@/api/hub'
import { getAIReportPlan, editReportTitle } from '@/api/ai'
import { saveReportPayment, getRemainJam } from '@/api/jam'
import { REPORT_TIMEMACHINE_DATA } from '@/libs/constants'
import { dateToLocalTimezone, utcToLocalTimezone } from '@/libs/utils'

import HeatmapTimeMachine from '@/components/heatmap/HeatmapTimeMachine'
import ScrollContent from '@/components/ui/ScrollContent'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'
import BackToTop from '@/components/ui/BackToTop'
import ReportTimeMachine from '@/components/reporting-heatmaps/ReportTimeMachine'
import AppDialog from '@/components/ui/AppDialog'
import AiReportPayment from '@/components/reporting-heatmaps/AiReportPayment'
import TimeMachineJamPaymentModal from '@/components/reporting-heatmaps/TimeMachineJamPaymentModal'
import Dimmed from '@/components/ui/Dimmed'
import ChargePv from '@/components/user-info/manage-plan/ChargePv'

export default {
  name: 'TimeMachineReport',
  components: {
    HeatmapTimeMachine,
    ScrollContent,
    UiLoadingCircle,
    BackToTop,
    ReportTimeMachine,
    AppDialog,
    AiReportPayment,
    TimeMachineJamPaymentModal,
    Dimmed,
    ChargePv
  },
  data () {
    return {
      isLoading: false,
      timeMachineData: [],
      popupType: null,
      pendingReservation: null,
      selectedReportData: null,
      topEventResult: null,
      captureImageData: null,
      replayResult: null,
      basicInfoResult: null,
      isShowReportPayment: false,
      reportPlan: null,
      reportItem: null,
      timemachineColumns: REPORT_TIMEMACHINE_DATA,
      currentSegment: {
        device: 'desktop',
        referrer: 'all',
        isNew: true,
        isReturn: true
      },
      selectedItem: null,
      isChargingJam: false,
      remainJam: 0,
      jamCost: 10
    }
  },
  computed: {
    ...mapState([
      'applyData',
      'userData',
      'planData'
    ]),
    aid () {
      return this.$route.params.aid
    },
    popupData () {
      if (this.popupType === 'success') {
        return {
          iconClass: 'icon--check-green-size50',
          title: this.$t('reporting.timeMachine.popup.successTitle'),
          description: this.$t('reporting.timeMachine.popup.successDescription'),
          type: 'alert'
        }
      }
      if (this.popupType === 'reportSuccess') {
        return {
          iconClass: 'icon--check-green-size50',
          title: this.$t('dialog.report.reportRequested.title'),
          description: this.$t('dialog.report.reportRequested.description'),
          type: 'mine'
        }
      }
      if (this.popupType === 'error') {
        return {
          iconClass: 'icon--alert-size50',
          title: this.$t('reporting.timeMachine.popup.errorTitle'),
          description: this.$t('reporting.timeMachine.popup.errorDescription') + '<br><br><span class="highlight">' + this.$t('reporting.timeMachine.popup.errorContact') + '</span>',
          type: 'alert'
        }
      }
      return {}
    },
    duplicateDateData () {
      return {
        iconClass: 'icon--alert-size50',
        title: this.$t('reporting.timeMachine.duplicateDate.title'),
        description: this.$t('reporting.timeMachine.duplicateDate.description'),
        type: 'confirm'
      }
    },
    reportDefaultName () {
      return this.$t('reporting.timeMachine.reportTitle')
    },
    reportApiParams () {
      if (!this.reportItem) return {}
      const seg = this.currentSegment
      const existing = this.getExistingFilter(seg)
      return {
        event: 'timemachine',
        startDate: this.reportItem.startDate,
        endDate: this.reportItem.endDate,
        device: seg.device,
        refer: seg.referrer,
        existing
      }
    }
  },
  created () {
    this.fetchList()
    getAIReportPlan().then(resp => {
      const param = { isPaid: true, promptType: 'single', reportType: 'segment', serviceType: 'beusable' }
      this.reportPlan = resp.find(obj =>
        Object.entries(param).every(([key, value]) => obj[key] === value)
      )
    })
  },
  methods: {
    toDateParam (str) {
      return str ? str.slice(0, 19) : str
    },
    getReportDateParams (item) {
      const startDate = this.toDateParam(item.startDate)
      const duration = Number(item.duration)
      if (!item.startDate || !Number.isFinite(duration) || duration <= 0) {
        return {
          startDate,
          endDate: this.toDateParam(item.endDate)
        }
      }
      const startDateValue = String(item.startDate)
      const normalizedStartDate = startDateValue.endsWith('Z') ? startDateValue : `${startDateValue}Z`
      const parsedStartDate = new Date(normalizedStartDate)
      if (isNaN(parsedStartDate.getTime())) {
        return {
          startDate,
          endDate: this.toDateParam(item.endDate)
        }
      }
      const endDate = new Date(parsedStartDate.getTime() + duration * 60000).toISOString().slice(0, 19)
      return {
        startDate,
        endDate
      }
    },
    toTzDateStr (dateStr, zeroSeconds = false) {
      if (!dateStr) return ''
      const str = String(dateStr).endsWith('Z') ? dateStr : dateStr + 'Z'
      const d = new Date(str)
      if (isNaN(d.getTime())) return this.toDateParam(dateStr)
      const tz = this.applyData.timezoneName
      const local = tz ? utcToLocalTimezone(d, tz) : d
      const y = local.getFullYear()
      const m = String(local.getMonth() + 1).padStart(2, '0')
      const day = String(local.getDate()).padStart(2, '0')
      const h = String(local.getHours()).padStart(2, '0')
      const min = String(local.getMinutes()).padStart(2, '0')
      const sec = zeroSeconds ? '00' : String(local.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${day}T${h}:${min}:${sec}`
    },
    getExistingFilter (segment) {
      if (!segment) return null
      if (segment.isNew && !segment.isReturn) return false
      if (!segment.isNew && segment.isReturn) return true
      return null
    },
    getSegmentParams (segment) {
      const refer = (segment && segment.referrer) || 'all'
      const existing = this.getExistingFilter(segment)
      if (existing === null) {
        return { refer }
      }
      return { refer, existing }
    },
    toSelectedReportData ({ item, replayResult, topEventResult }) {
      const replay = replayResult || {}
      const frames = Array.isArray(replay.frames) ? replay.frames : []
      return {
        startDate: item.startDate,
        endDate: item.endDate,
        duration: item.duration,
        pvData: frames.map(frame => ({
          activePv: frame.activePv || 0,
          totalPv: frame.totalPv || 0,
          totalEventCount: frame.totalEventCount || 0,
          timestamp: frame.timestamp
        })),
        replayData: replay,
        topEventData: topEventResult || null,
        totalEventCount: replay.totalEventCount || 0,
        totalPv: replay.totalPv || 0
      }
    },
    fetchList () {
      this.isLoading = true
      getTimeMachineList({ aid: this.aid }).then(res => {
        this.timeMachineData = Array.isArray(res && res.result) ? res.result : []
      }).catch(error => {
        console.error('fetchList error:', error)
      }).finally(() => {
        this.isLoading = false
      })
    },
    onSelectReport (item) {
      if (!item) {
        this.selectedReportData = null
        this.topEventResult = null
        this.replayResult = null
        this.basicInfoResult = null
        this.selectedItem = null
        return
      }
      this.selectedItem = item
      this.fetchReportData(item)
    },
    fetchReportData (item) {
      const startDate = this.toTzDateStr(item.startDate, true)
      const endDate = this.toTzDateStr(item.endDate, true)
      const seg = this.currentSegment
      const segParams = this.getSegmentParams(seg)
      const replayParams = {
        aid: this.aid,
        device: seg.device,
        interval: 1,
        startDate,
        endDate,
        ...segParams
      }
      Promise.allSettled([
        getReplay(replayParams),
        getTopEvent({ aid: this.aid, device: seg.device, startDate, endDate, ...segParams }),
        getBasicInfo({ aid: this.aid, device: seg.device, startDate, endDate, existing: true })
      ]).then(([replayRes, topEventRes, basicInfoRes]) => {
        const replayResult = replayRes.status === 'fulfilled' && replayRes.value && replayRes.value.result
          ? replayRes.value.result : {}
        const topEventValue = topEventRes.status === 'fulfilled' && topEventRes.value && topEventRes.value.result
          ? topEventRes.value.result : null
        const basicInfoValue = basicInfoRes.status === 'fulfilled' ? basicInfoRes.value : null

        this.topEventResult = topEventValue
        this.replayResult = replayResult
        this.basicInfoResult = basicInfoValue && basicInfoValue.result ? basicInfoValue.result : null
        this.selectedReportData = this.toSelectedReportData({
          item,
          replayResult,
          topEventResult: topEventValue || null
        })
      })
    },
    convertToUTCParams (data) {
      const localDate = new Date(
        data.startDate.getFullYear(),
        data.startDate.getMonth(),
        data.startDate.getDate(),
        Number(data.hour),
        Number(data.minute)
      )
      const utcStart = dateToLocalTimezone(localDate, this.applyData.timezoneName)
      const utcEnd = new Date(utcStart.getTime() + data.duration * 60000)

      return {
        startDate: utcStart.toISOString(),
        endDate: utcEnd.toISOString()
      }
    },
    onAddReservation (data) {
      this.submitReservation(data)
    },
    onAddReservationWithCredit (data) {
      this.pendingReservation = data
      this.popupType = 'jamPayment'
    },
    onDuplicateDate () {
      this.popupType = 'duplicateDate'
    },
    submitReservation (data) {
      const utc = this.convertToUTCParams(data)
      addTimeMachine({
        aid: this.aid,
        startDate: utc.startDate,
        endDate: utc.endDate,
        duration: data.duration,
        status: 'reservation',
        timezone: data.timezone,
        timezoneName: data.timezoneName
      }).then(res => {
        if (res && res.success) {
          this.popupType = 'success'
          this.fetchList()
        } else {
          this.popupType = 'error'
        }
      }).catch(() => {
        this.popupType = 'error'
      })
    },
    onDuplicateDateResponse (value) {
      this.popupType = null
      if (value) {
        this.$refs.reportTimeMachine.openModalWithCredit()
      }
    },
    onJamPaymentClose () {
      this.popupType = null
      this.pendingReservation = null
    },
    onJamPaymentBack () {
      const reservation = this.pendingReservation
      this.popupType = null
      this.pendingReservation = null
      if (reservation && this.$refs.reportTimeMachine) {
        this.$refs.reportTimeMachine.reopenReservationModal(reservation)
      }
    },
    onJamPaymentConfirm () {
      this.popupType = null
      if (!this.pendingReservation) return
      const params = {
        variantKey: 'jam',
        serviceType: 'beusable',
        price: 10,
        jamState: 'BUY_ADD_TIMEMACHINE'
      }
      saveReportPayment(params, true).then(resp => {
        if (resp) {
          this.submitReservation(this.pendingReservation)
          this.pendingReservation = null
        } else {
          this.popupType = 'error'
          this.pendingReservation = null
        }
      }).catch(() => {
        this.popupType = 'error'
        this.pendingReservation = null
      })
    },
    onChargeJam (remainJam) {
      this.popupType = null
      this.remainJam = remainJam
      this.isChargingJam = true
    },
    onModifyReservation (data) {
      const utc = this.convertToUTCParams(data)
      modifyTimeMachine({
        id: data.id,
        aid: this.aid,
        startDate: utc.startDate,
        endDate: utc.endDate,
        duration: data.duration,
        timezone: data.timezone,
        timezoneName: data.timezoneName
      }).then(res => {
        if (res && res.success) {
          this.popupType = 'success'
          this.fetchList()
        } else {
          this.popupType = 'error'
        }
      }).catch(() => {
        this.popupType = 'error'
      })
    },
    onDeleteReservation (item) {
      deleteTimeMachine({ id: item._id, aid: this.aid }).then(() => {
        this.fetchList()
      }).catch(error => {
        console.error('deleteTimeMachine error:', error)
      })
    },
    onImageLoaded (data) {
      this.captureImageData = data
    },
    onSegmentChange (segment) {
      this.currentSegment = segment
      if (this.selectedItem) {
        this.fetchReportData(this.selectedItem)
      }
    },
    onCreateReport (item) {
      this.reportItem = item
      getRemainJam(null, true).then(() => {
        this.isShowReportPayment = true
      })
    },
    createReport (item, lang, title, paymentId) {
      const seg = this.currentSegment
      const segParams = this.getSegmentParams(seg)
      const reportDateParams = this.getReportDateParams(item)
      const params = {
        aid: this.aid,
        device: seg.device,
        startDate: reportDateParams.startDate,
        endDate: reportDateParams.endDate,
        bab: false,
        interval: 1,
        title: title,
        lang: lang,
        reportType: 'replay',
        promptType: 'single',
        pv: 0,
        isOnlyData: false,
        ...segParams
      }
      let reportId = null
      return createTimeMachineReport(params)
        .then(resp => {
          if (!resp || resp.success === false) {
            const message = resp && resp.error && resp.error.message
              ? resp.error.message
              : 'CREATE_REPORT_DATA_FAILED'
            throw new Error(message)
          }
          reportId = resp && resp.result ? resp.result.aiReportId : null
          if (!reportId) {
            throw new Error('AI_REPORT_ID_NOT_FOUND')
          }
          return editReportTitle({ reportId: reportId, title: title })
        })
        .then(() => createCXReport({ id: reportId, paid: true, paymentId: paymentId, lang: lang }))
    },
    onReportPayment (payload) {
      const params = {
        variantKey: payload.paymentType,
        serviceType: 'beusable',
        price: payload.paymentType === 'jam' ? payload.price : payload.price * 1.1,
        jamState: 'BUY_REPORT_TIMEMACHINE'
      }
      saveReportPayment(params, true).then(resp => {
        if (!resp) {
          this.popupType = 'error'
          return
        }
        this.isShowReportPayment = false
        const paymentId = resp._id || resp.paymentId
        this.createReport(this.reportItem, payload.lang, payload.title, paymentId).then(createResp => {
          if (createResp) {
            this.popupType = 'reportSuccess'
          } else {
            this.popupType = 'error'
          }
        }).catch(() => {
          this.popupType = 'error'
        })
      }).catch(() => {
        this.popupType = 'error'
      })
    },
    onPopupClose (value) {
      if (this.popupType === 'reportSuccess' && value === true) {
        window.open('/mine', '_blank')
      }
      this.popupType = null
    },
    onReportChargeJam (remainJam) {
      this.isShowReportPayment = false
      this.remainJam = remainJam
      this.isChargingJam = true
    }
  }
}
</script>
